import { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI, carrierAPI } from '../../lib/api';
import { ManagerSelector } from '@/components/ManagerSelector';
import { PayeeSelector } from '@/components/PayeeSelector';
import { fmt } from './_shared';

function RuleDialog({ open, onClose, rule, onSaved }) {
  const [form, setForm] = useState({
    name: '', payee_type: 'Agency', compensation_type: 'flat',
    calculation_basis: 'actual_paid', allow_proration: true,
    amount: '', percentage: '', per_unit_amount: '',
    carrier_id: '', product_category: '', product_id: '',
    agency_id: '', payee_id: '', notes: '', active: true,
  });
  const [carriers, setCarriers] = useState([]);

  useEffect(() => {
    if (!open) return;
    carrierAPI.list().then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.carriers || []);
      setCarriers(list);
    }).catch(() => setCarriers([]));
  }, [open]);

  useEffect(() => {
    if (rule) {
      setForm({
        name: rule.name || '',
        payee_type: rule.payee_type || 'Agency',
        compensation_type: rule.compensation_type || 'flat',
        calculation_basis: rule.calculation_basis || 'actual_paid',
        allow_proration: rule.allow_proration !== false,
        amount: rule.amount ?? '',
        percentage: rule.percentage ?? '',
        per_unit_amount: rule.per_unit_amount ?? '',
        carrier_id: rule.carrier_id || '',
        product_category: rule.product_category || '',
        product_id: rule.product_id || '',
        agency_id: rule.agency_id || '',
        payee_id: rule.payee_id || '',
        carrier_expected_amount: rule.carrier_expected_amount ?? '',
        carrier_expected_per_member: rule.carrier_expected_per_member ?? '',
        notes: rule.notes || '',
        active: rule.active !== false,
      });
    } else {
      setForm({
        name: '', payee_type: 'Agency', compensation_type: 'flat',
        calculation_basis: 'actual_paid', allow_proration: true,
        amount: '', percentage: '', per_unit_amount: '',
        carrier_id: '', product_category: '', product_id: '',
        agency_id: '', payee_id: '',
        carrier_expected_amount: '', carrier_expected_per_member: '',
        notes: '', active: true,
      });
    }
  }, [rule, open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const payload = { ...form };
    ['amount', 'percentage', 'per_unit_amount', 'carrier_expected_amount', 'carrier_expected_per_member'].forEach((k) => {
      payload[k] = payload[k] === '' ? null : Number(payload[k]);
    });
    Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
    try {
      if (rule) {
        await compensationAPI.updateRule(rule.rule_id, payload);
        toast.success('Rule updated');
      } else {
        await compensationAPI.createRule(payload);
        toast.success('Rule created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit Rule' : 'New Compensation Rule'}</DialogTitle>
          <DialogDescription>Rules resolve by specificity. Constrain by carrier/product/agency/payee to scope tighter.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Rule Name</Label>
            <Input data-testid="rule-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Payee Type</Label>
            <Select value={form.payee_type} onValueChange={(v) => setForm({ ...form, payee_type: v })}>
              <SelectTrigger data-testid="rule-payee-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Agency">Agency (override)</SelectItem>
                <SelectItem value="Manager">Manager / Development Leader</SelectItem>
                <SelectItem value="Agent">Agent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Compensation Type</Label>
            <Select value={form.compensation_type} onValueChange={(v) => setForm({ ...form, compensation_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="flat">Flat amount</SelectItem>
                <SelectItem value="percentage">Percentage</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Calculation Basis</Label>
            <Select value={form.calculation_basis} onValueChange={(v) => setForm({ ...form, calculation_basis: v })}>
              <SelectTrigger data-testid="rule-basis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="actual_paid">Actual paid by carrier</SelectItem>
                <SelectItem value="expected">Expected (prorated when applicable)</SelectItem>
                <SelectItem value="prorated">Prorated (expected × ratio)</SelectItem>
                <SelectItem value="per_member">Per member (fixed)</SelectItem>
                <SelectItem value="per_policy">Per policy (fixed)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Flat Amount</Label>
            <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="e.g. 50.00" />
          </div>
          <div>
            <Label>Percentage</Label>
            <Input type="number" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} placeholder="e.g. 10" />
          </div>
          <div>
            <Label>Per-unit Amount</Label>
            <Input type="number" value={form.per_unit_amount} onChange={(e) => setForm({ ...form, per_unit_amount: e.target.value })} placeholder="e.g. 3 (per member) or 25 (per policy)" />
          </div>
          <div className="flex items-center gap-3 mt-6">
            <Switch checked={form.allow_proration} onCheckedChange={(v) => setForm({ ...form, allow_proration: v })} data-testid="rule-allow-proration" />
            <Label>Allow proration on mismatch</Label>
          </div>
          <div>
            <Label>Carrier (scope)</Label>
            <Select value={form.carrier_id || '__all__'} onValueChange={(v) => setForm({ ...form, carrier_id: v === '__all__' ? '' : v })}>
              <SelectTrigger data-testid="rule-carrier-select"><SelectValue placeholder="All carriers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All carriers</SelectItem>
                {carriers.map((c) => (
                  <SelectItem key={c.carrier_id || c.id} value={c.carrier_id || c.id}>
                    {c.carrier_name || c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Product Category (scope)</Label>
            <Select value={form.product_category || '__all__'} onValueChange={(v) => setForm({ ...form, product_category: v === '__all__' ? '' : v })}>
              <SelectTrigger data-testid="rule-product-category-select"><SelectValue placeholder="All product categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All product categories</SelectItem>
                <SelectItem value="ACA">ACA</SelectItem>
                <SelectItem value="Medicare">Medicare</SelectItem>
                <SelectItem value="Supplemental">Supplemental</SelectItem>
                <SelectItem value="Life">Life</SelectItem>
                <SelectItem value="Final Expense">Final Expense</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Product ID (scope) <span className="text-xs text-slate-500 ml-1">(optional)</span></Label>
            <Input value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} placeholder="Specific product (advanced)" />
          </div>
          <div>
            <Label>Agency (scope) <span className="text-xs text-slate-500 ml-1">(optional)</span></Label>
            <PayeeSelector
              kind="agency"
              value={form.agency_id}
              onChange={(id) => setForm({ ...form, agency_id: id || '' })}
              placeholder="All agencies (leave empty)"
              testId="rule-agency-scope-selector"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Specific Payee <span className="text-xs text-slate-500 ml-1">(optional — narrows the rule to one payee)</span></Label>
            {form.payee_type === 'Manager' ? (
              <ManagerSelector
                value={form.payee_id}
                onChange={(id) => setForm({ ...form, payee_id: id || '' })}
                testId="rule-manager-selector"
                placeholder="Any manager (leave empty to match all)"
              />
            ) : form.payee_type === 'Agent' ? (
              <PayeeSelector
                kind="agent"
                value={form.payee_id}
                onChange={(id) => setForm({ ...form, payee_id: id || '' })}
                placeholder="Any agent (leave empty to match all)"
                testId="rule-agent-payee-selector"
              />
            ) : (
              <PayeeSelector
                kind="agency"
                value={form.payee_id}
                onChange={(id) => setForm({ ...form, payee_id: id || '' })}
                placeholder="Any agency (leave empty to match all)"
                testId="rule-agency-payee-selector"
              />
            )}
          </div>
          <div className="flex items-center gap-3 mt-6">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label>Active</Label>
          </div>
          <div className="md:col-span-2 border-t pt-4 mt-2">
            <div className="text-sm font-semibold text-zinc-800 mb-1">Carrier-side expected (optional)</div>
            <div className="text-xs text-zinc-500 mb-3">
              When a statement file does <strong>not</strong> include the expected/booked amount, the engine uses these to derive it during import.
              These describe what the <strong>carrier</strong> owes you per policy/member — NOT what you pay the agent.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Carrier expected — flat per policy ($)</Label>
                <Input type="number" step="0.01" value={form.carrier_expected_amount}
                       onChange={(e) => setForm({ ...form, carrier_expected_amount: e.target.value })}
                       placeholder="e.g. 25.00"
                       data-testid="rule-carrier-expected-flat" />
              </div>
              <div>
                <Label>Carrier expected — per member per month ($)</Label>
                <Input type="number" step="0.01" value={form.carrier_expected_per_member}
                       onChange={(e) => setForm({ ...form, carrier_expected_per_member: e.target.value })}
                       placeholder="e.g. 3.00"
                       data-testid="rule-carrier-expected-per-member" />
              </div>
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              If both are set, per-member wins when the record has a member count.
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="rule-save-btn">{rule ? 'Update' : 'Create'} Rule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await compensationAPI.listRules();
      setRules(r.data.rules || []);
    } catch { toast.error('Failed to load rules'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="comp-rules-new">+ New Rule</Button>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="comp-rules-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
        <span className="text-sm text-zinc-500 ml-auto">{rules.length} rules</span>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Payee</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Basis</TableHead>
              <TableHead>Amount/%</TableHead>
              <TableHead>Proration</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules.length === 0 && !loading && (
              <TableRow><TableCell colSpan={9} className="text-center text-zinc-500 py-6">No rules defined yet.</TableCell></TableRow>
            )}
            {rules.map((r) => (
              <TableRow key={r.rule_id} data-testid={`comp-rule-row-${r.rule_id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><Badge variant="outline">{r.payee_type}</Badge>{r.payee_id && <span className="ml-1 text-xs text-zinc-500">@ {r.payee_id}</span>}</TableCell>
                <TableCell className="text-sm">{r.carrier_id || '—'}</TableCell>
                <TableCell className="text-sm">{r.product_category || r.product_id || '—'}</TableCell>
                <TableCell className="text-sm">{r.calculation_basis}</TableCell>
                <TableCell className="text-sm">
                  {r.compensation_type === 'flat' && fmt(r.amount)}
                  {r.compensation_type === 'percentage' && `${r.percentage}%`}
                  {['per_member', 'per_policy'].includes(r.calculation_basis) && r.per_unit_amount && `${fmt(r.per_unit_amount)}/unit`}
                </TableCell>
                <TableCell className="text-sm">{r.allow_proration ? 'Yes' : 'No'}</TableCell>
                <TableCell>{r.active ? <Badge className="bg-emerald-100 text-emerald-800">Active</Badge> : <Badge>Inactive</Badge>}</TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`comp-rule-edit-${r.rule_id}`}>Edit</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <RuleDialog open={dialogOpen} onClose={() => setDialogOpen(false)} rule={editing} onSaved={() => { setDialogOpen(false); load(); }} />
    </div>
  );
}
